import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Group,
  Stack,
  Button,
  ActionIcon,
  TextInput,
  Table,
  Chip,
  Menu,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { ReactComponent as MoreIcon } from "@/assets/icons/more.svg";
import { ReactComponent as BackIcon } from "@/assets/icons/back.svg";
import { ReactComponent as CheckIcon } from "@/assets/icons/check.svg";
import { ReactComponent as MinusIcon } from "@/assets/icons/minus.svg";
import { ReactComponent as DeleteIcon } from "@/assets/icons/delete.svg";
import AreYouSureModal from "@/components/Shared/AreYouSureModal/AreYouSureModal";
import { findActiveSubscription } from "@/helpers/issuesAndSubscriptions";
import {
  successNotification,
  errorNotification,
} from "@/helpers/notifications";
import { UserWithSubscriptions, Order } from "@/api/types";
import { OrderApi } from "@/api/order.api";
import { UserApi } from "@/api/user.api";
import dayjs from "dayjs";
import "./UserDetails.css";

const UserDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [user, setUser] = useState<UserWithSubscriptions | null>();
  const [orderItems, setOrderItems] = useState<Order["orderItems"]>();
  const [menuOpened, setMenuOpened] = useState(false);
  const [opened, { open, close }] = useDisclosure(false);
  const toggleMenu = () => setMenuOpened((prev) => !prev);

  const getUser = async () => {
    const result = await UserApi.getUserById(Number(id));
    setUser(result);
  };

  const getUserOrders = async () => {
    const orderData = await OrderApi.getUserOrders(Number(id));
    if (orderData) {
      const orderItems = orderData.map((order) => order.orderItems).flat();
      setOrderItems(orderItems);
    }
  };

  useEffect(() => {
    getUser();
    getUserOrders();
  }, []);

  useEffect(() => {
    if (user) {
      form.setValues({
        firstName: user.firstName ?? "",
        lastName: user.lastName ?? "",
        email: user.email ?? "",
      });
    }
  }, [user]);

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      email: user?.email ?? "",
    },
    validate: {
      firstName: (value: string) => (!value ? "Ime je obavezno" : null),
      lastName: (value: string) => (!value ? "Prezime je obavezno" : null),
      email: (value: string) =>
        !value
          ? "E-mail je obavezan"
          : /^\S+@\S+$/.test(value)
          ? null
          : "E-mail nije ispravan",
    },
  });

  const saveUserDetails = async (values: typeof form.values) => {
    try {
      await UserApi.updateUser(Number(id), values);
      getUser();
      successNotification({
        message: "Promjene uspješno spremljene",
      });
    } catch (error) {
      errorNotification({
        message: "Došlo je do pogreške! Molimo pokušajte ponovno.",
      });
    }
  };

  const deleteUser = async () => {
    close();
    const res = await UserApi.deleteUser(Number(id));
    if (res?.isSuccessful) {
      successNotification({
        message: res.message,
      });
      navigate("/admin/users");
    } else {
      errorNotification({
        message: res.message,
      });
    }
  };

  return (
    <div className="px-[5%]">
      <form onSubmit={form.onSubmit(saveUserDetails)}>
        <Group className="py-[1.25rem]" justify="space-between">
          <Group
            className="cursor-pointer hover:opacity-80"
            onClick={() => navigate("/admin/users")}
          >
            <ActionIcon
              variant="outline"
              color="#E6E6E6"
              size="xl"
              radius="xl"
              aria-label="Settings"
            >
              <BackIcon fill="#0B1A29" />
            </ActionIcon>
            <div className="text-[#0b1a29] text-base font-bold">Natrag</div>
          </Group>
          <Group className="actions-group">
            <Button
              type="submit"
              variant="filled"
              color="#004a99"
              radius="xl"
              size="md"
              className="!h-[3rem]"
            >
              Spremi promjene
            </Button>
            <Menu
              shadow="lg"
              opened={menuOpened}
              onClose={() => setMenuOpened(false)}
              position="bottom-end"
              classNames={{
                dropdown: "more-dropdown",
                item: "more-item",
              }}
            >
              <Menu.Target>
                <ActionIcon
                  variant="outline"
                  color="gray"
                  size="xl"
                  radius="xl"
                  aria-label="Settings"
                  onClick={toggleMenu}
                >
                  <MoreIcon />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<DeleteIcon className="text-[#f40c0c]" />}
                  onClick={() => open()}
                >
                  <span className="font-medium text-[#f40c0c]">
                    {" "}
                    Obriši račun
                  </span>
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
        <Group>
          <div className="text-[#0b1a29] text-xl font-bold">
            Informacije o korisniku
          </div>
        </Group>
        <div className="user-details-container">
          <div className="info-subscription-container">
            <Stack className="personal-info">
              <div className="text-[#0b1a29] text-base font-bold">
                Osobne informacije
              </div>
              <Stack className="h-max">
                <div className="name-group">
                  <TextInput
                    className="flex-1"
                    label="Ime"
                    placeholder="Ime"
                    key={form.key("firstName")}
                    {...form.getInputProps("firstName")}
                  />
                  <TextInput
                    className="flex-1"
                    label="Prezime"
                    placeholder="Prezime"
                    key={form.key("lastName")}
                    {...form.getInputProps("lastName")}
                  />
                </div>
                <TextInput
                  label="E-mail"
                  placeholder="email@mail.com"
                  key={form.key("email")}
                  {...form.getInputProps("email")}
                />
              </Stack>
            </Stack>
            <Stack className="subscription">
              <div className="text-[#0b1a29] text-base font-bold">
                Pretplata
              </div>
              <Stack className="h-max" gap={0}>
                <div className="p-4 border-b flex justify-between items-center">
                  <div className="text-[#515151] text-sm font-medium">
                    Status
                  </div>
                  <div className="text-right text-[#0b1a29] text-sm font-bold">
                    <Chip
                      checked={true}
                      color={
                        user && !!findActiveSubscription(user.userSubscriptions)
                          ? "#2c973f"
                          : "#515151"
                      }
                      icon={
                        user &&
                        !!findActiveSubscription(user.userSubscriptions) ? (
                          <CheckIcon />
                        ) : (
                          <MinusIcon />
                        )
                      }
                      variant="light"
                      className="!cursor-default font-bold"
                      classNames={{ label: "!py-[1rem]" }}
                    >
                      {user && !!findActiveSubscription(user.userSubscriptions)
                        ? "Aktivna"
                        : "Neaktivna"}
                    </Chip>
                  </div>
                </div>
                <div className="p-4 border-b flex justify-between">
                  <div className="text-[#515151] text-sm font-medium">
                    Datum aktivacije pretplate
                  </div>
                  <div className="text-right text-[#0b1a29] text-sm font-bold">
                    {(user &&
                      !!findActiveSubscription(user.userSubscriptions) &&
                      dayjs(
                        findActiveSubscription(user.userSubscriptions)
                          ?.startDate
                      ).format("DD.MM.YYYY.")) ||
                      "-"}
                  </div>
                </div>
                <div className="p-4 border-b flex justify-between">
                  <div className="text-[#515151] text-sm font-medium">
                    Datum isteka pretplate
                  </div>
                  <div className="text-right text-[#0b1a29] text-sm font-bold">
                    {(user &&
                      !!findActiveSubscription(user.userSubscriptions) &&
                      dayjs(
                        findActiveSubscription(user.userSubscriptions)?.endDate
                      ).format("DD.MM.YYYY.")) ||
                      "-"}
                  </div>
                </div>
              </Stack>
            </Stack>
          </div>
          <Group className="!w-full">
            <Stack className="flex-1">
              <div className="text-[#0b1a29] text-base font-bold">
                Povijest kupovine
              </div>
              {orderItems?.length ? (
                <div className="border border-[#eaeaea] rounded-lg overflow-hidden">
                  <Table>
                    <Table.Thead className="bg-[#f9f9f9]">
                      <Table.Tr>
                        <Table.Th className="!p-4">Artikl</Table.Th>
                        <Table.Th>Cijena</Table.Th>
                        <Table.Th>Datum kupovine</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody className="">
                      {orderItems.map((item) => (
                        <Table.Tr key={item.id}>
                          <Table.Td className="!p-3.5">
                            {item.subscription
                              ? item.subscription.title
                              : item.issue
                              ? item.issue?.title
                              : item.publication?.title}
                          </Table.Td>
                          <Table.Td>{item.price}€</Table.Td>
                          <Table.Td>
                            {dayjs(item.createdAt).format("DD.MM.YYYY.")}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </div>
              ) : (
                <div className="flex justify-start items-start text-[#515151] text-sm font-medium">
                  Trenutno nema kupnji
                </div>
              )}
            </Stack>
          </Group>
          <div className="mobile-save-button">
            <Button
              type="submit"
              className="flex-1 !h-[3.25rem]"
              variant="filled"
              color="#004a99"
              radius="xl"
              size="md"
            >
              Spremi promjene
            </Button>
          </div>
        </div>
      </form>
      <AreYouSureModal
        opened={opened}
        message="Jeste li sigurni da želite obrisati ovaj korisnički račun? Korisnik ga
        više neće moći koristiti!"
        onClose={close}
        onAction={deleteUser}
      />
    </div>
  );
};

export default UserDetails;
